import React, { useRef, useEffect, useState } from 'react';
import ContentItem from './ContentItem';
import { useNavigate } from 'react-router-dom';

interface ContentRowProps {
  title: string;
  items: any[];
  category: string;
  onPreview?: (embedId: string) => void;
  fullWidth?: boolean;
}

const ContentRow: React.FC<ContentRowProps> = ({
  title,
  items,
  category,
  onPreview,
  fullWidth = false,
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [, forceUpdate] = useState({});

  const currentUser = localStorage.getItem('user') || 'anonymous';

  useEffect(() => {
    const interval = setInterval(() => {
      forceUpdate({});
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (rowRef.current) {
      const { scrollLeft, clientWidth } = rowRef.current;
      const scrollTo =
        direction === 'left'
          ? scrollLeft - clientWidth / 2
          : scrollLeft + clientWidth / 2;

      rowRef.current.scrollTo({
        left: scrollTo,
        behavior: 'smooth',
      });
    }
  };

  const handleIniciarContagem = (item: any) => {
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 48);
    const alugadosAll = JSON.parse(localStorage.getItem('alugados') || '{}');
    const userAlugados = alugadosAll[currentUser] || {};
    userAlugados[item.id] = { expiresAt: expiresAt.toISOString(), started: true };
    alugadosAll[currentUser] = userAlugados;
    localStorage.setItem('alugados', JSON.stringify(alugadosAll));
    forceUpdate({});
  };

  const handleAlugar = (item: any) => {
    const alugadosAll = JSON.parse(localStorage.getItem('alugados') || '{}');
    const userAlugados = alugadosAll[currentUser] || {};
    if (!userAlugados[item.id]) {
      userAlugados[item.id] = { started: false };
      alugadosAll[currentUser] = userAlugados;
      localStorage.setItem('alugados', JSON.stringify(alugadosAll));
    }

    navigate(`/pagamento-aluguel/${item.id}`, {
      state: {
        videoTitle: item.title,
        videoId: item.id,
        videoPrice: item.price,
        videoSrc: item.videoSrc,
        embedId: item.embedId,
      },
    });
  };

  const isAlugadoValido = (id: string) => {
    const alugadosAll = JSON.parse(localStorage.getItem('alugados') || '{}');
    const userAlugados = alugadosAll[currentUser] || {};
    const info = userAlugados[id];
    if (!info || !info.expiresAt || !info.started) return false;
    return new Date() < new Date(info.expiresAt);
  };

  const isMarcadoParaContagem = (id: string) => {
    const alugadosAll = JSON.parse(localStorage.getItem('alugados') || '{}');
    const userAlugados = alugadosAll[currentUser] || {};
    return userAlugados[id] && !userAlugados[id].started;
  };

  return (
    <div className={`mb-6 relative ${fullWidth ? '' : 'max-w-screen-xl mx-auto'}`}>
      <h2 className="title_topico text-white font-bold text-lg mb-2 px-6">{title}</h2>

      <div className="content relative">
        <button
          onClick={() => scroll('left')}
          className="scroll-left absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 rounded-full z-10 flex items-center justify-center hover:bg-black/80 transition"
        >
          <span className="material-symbols-outlined text-white">chevron_left</span>
        </button>

        <div
          ref={rowRef}
          className="content-row scrollbar-hide flex overflow-x-auto gap-4 px-12"
        >
          {items.map((item) => {
            const alugadoValido = isAlugadoValido(item.id);
            const alugadoMarcado = isMarcadoParaContagem(item.id);

            return (
              <div key={item.id} className="relative flex flex-col items-center">
                <ContentItem item={item} category={category} onPreview={onPreview} />

                {item.price && (
                  <div className="w-1/3 mt-2">
                    {alugadoValido ? (
                      <button
                        className="w-full px-3 py-1 bg-green-700 text-white text-xs font-medium rounded transition flex items-center justify-center gap-1 cursor-default"
                        disabled
                      >
                        <span className="material-symbols-outlined text-white text-sm">check_circle</span>
                        Alugado (válido)
                      </button>
                    ) : alugadoMarcado ? (
                      <button
                        onClick={() => handleIniciarContagem(item)}
                        className="w-full px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded transition flex items-center justify-center gap-1"
                      >
                        <span className="material-symbols-outlined text-white text-sm">play_arrow</span>
                        Iniciar 48h
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAlugar(item)}
                        className="w-full px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition"
                      >
                        Alugar por R$ {item.price},00
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button
          onClick={() => scroll('right')}
          className="scroll-right absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 rounded-full z-10 flex items-center justify-center hover:bg-black/80 transition"
        >
          <span className="material-symbols-outlined text-white">chevron_right</span>
        </button>
      </div>
    </div>
  );
};

export default ContentRow;
